import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Cloud, CheckCircle, ArrowRight, Server, Database, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';

const CloudMigrationPage = () => {
  const benefits = [
    'Zero downtime migration strategy',
    'Automated workload assessment',
    'Cost optimization during migration',
    'Security and compliance maintained',
    'Expert 24/7 migration support',
    'Performance optimization post-migration',
  ];

  const steps = [
    {
      icon: Server,
      title: 'Assessment & Planning',
      description: 'Comprehensive analysis of your current infrastructure and workloads',
    },
    {
      icon: Database,
      title: 'Migration Strategy',
      description: 'Customized migration approach based on your business requirements',
    },
    {
      icon: Cloud,
      title: 'Execution & Testing',
      description: 'Seamless migration with continuous testing and validation',
    },
    {
      icon: Shield,
      title: 'Optimization & Support',
      description: 'Post-migration optimization and ongoing managed services',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020B1A] to-[#061426]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] overflow-hidden text-white pt-32 pb-24 mt-16">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Cloud Migration Services
            </h1>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Seamlessly migrate your workloads to AWS with zero downtime and optimized performance. Our proven methodology ensures a smooth transition to the cloud.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-8 py-6 text-lg transition-all duration-300" style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>
                Start Your Migration Journey
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-[#061426] to-[#020B1A]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Our Migration Services</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Experience a hassle-free migration with our comprehensive approach
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gradient-to-br from-[#061426] to-[#0A1E3F] border border-cyan-500/20 p-6 rounded-lg">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <span className="text-white text-lg">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process */}
      <section className="py-20 bg-gradient-to-b from-[#020B1A] to-[#061426]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Migration Process</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A proven 4-step approach to successful cloud migration
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="bg-gradient-to-b from-[#061426] to-[#020B1A] rounded-xl p-8 shadow-md transition-all duration-300 hover:shadow-xl">
                  <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-cyan-400 font-bold text-lg mb-3">Step {index + 1}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] overflow-hidden text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Migrate to AWS?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            Let's discuss your migration strategy and create a customized plan
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-10 py-6 text-lg transition-all duration-300" style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>
              Get Free Migration Assessment
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudMigrationPage;